import { useEffect, useState } from 'react';

import CommentIcon from '@mui/icons-material/Comment';
import InfoIcon from '@mui/icons-material/Info';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import TitleIcon from '@mui/icons-material/Title';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Button } from '@mui/material';
import jwtDecode from 'jwt-decode';
import {
  Link,
  useHistory,
  useParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';

import { RouterParams } from 'containers/AppRouter';
import ButtonActionActiveStatus from 'shared/components/Button/Action/ButtonActionActiveStatus/ButtonActionActiveStatus';
import ButtonActionDeleteEnforceConfirm from 'shared/components/Button/Action/ButtonActionDelete/ButtonActionDeleteEnforceConfirm';
import TabsOrganize, { TabT } from 'shared/components/TabsOrganize';
import GenericUsers from 'shared/containers/GenericUsers/GenericUsers';
import Loader from 'shared/containers/Loader';
import ViewDetails from 'shared/containers/ViewDetails';
import { GENERAL_INFO_TITLE, SETTINGS_TITLE, ViewDetailsT } from 'shared/containers/ViewDetails/ViewDetails';
import { APIErrorT } from 'shared/models/api';
import { OrganizationT, XXTEATokenT } from 'shared/models/organizations';
import api from 'utils/api';
import { formatDateFromNow } from 'utils/date';
import { isProductionEnv } from 'utils/general';

function OrganizationsDetails() {
  const { organizationName } = useParams() as RouterParams;
  const [isLoading, setIsLoading] = useState(true);
  const [details, setDetails] = useState<OrganizationT | null>(null);
  const history = useHistory();

  const formatXXTeaToken = (token: string): string => {
    if (!token) return '';
    const decodedToken: XXTEATokenT = jwtDecode(token);
    return `${decodedToken.XXTEAKey} (expires ${formatDateFromNow(new Date(decodedToken.exp * 1000).toString())})`;
  };

  const dataStructure: ViewDetailsT = {
    sections: [
      {
        title: GENERAL_INFO_TITLE,
        items: [
          {
            label: 'Display Name',
            dataPath: 'displayName',
            icon: <TitleIcon color="primary" />,
          },
          {
            type: 'creation',
          },
          {
            label: 'Description',
            dataPath: 'description',
            icon: <CommentIcon color="primary" />,
          },
          {
            type: 'update',
          },
        ],
        shape: 2,
      },
      {
        items: [
          {
            label: 'Encryption Enabled',
            dataPath: 'enableEncryption',
          },
          {
            label: 'XXTEA Encryption Key',
            dataPath: 'xxteaKeyToken',
            renderFn: formatXXTeaToken,
            hideOnEmpty: true,
          },
        ],
        shape: 1,
      },
      {
        title: SETTINGS_TITLE,
        items: [
          {
            label: 'Maximum number of devices',
            dataPath: 'deviceLimit',
          },
          {
            label: 'Maximum numbers of IP networks',
            dataPath: 'poolLimit',
            renderFn: (value: string) => (value === '0' ? 'Unlimited' : value),
          },
          {
            label: 'Maximum numbers of sources',
            dataPath: 'sourceLimit',
            renderFn: (value: string) => (value === '0' ? 'Unlimited' : value),
          },
        ],
        shape: 1,
      },
    ],
  };

  const tabsContent: TabT[] = [
    {
      label: 'Details',
      name: 'details',
      content: (
        <div style={{ overflowX: 'scroll' }}>
          <ViewDetails dataStructure={dataStructure} data={details} />
        </div>
      ),
      icon: <InfoIcon />,
      iconPosition: 'start',
    },
    {
      label: 'Users',
      name: 'users',
      content: <GenericUsers />,
      icon: <PeopleAltIcon />,
      iconPosition: 'start',
    },
  ];

  const loadData = () => {
    setIsLoading(true);
    api.organizations.getOne(organizationName as string)
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              const redirectTo = '/organizations';
              console.warn('Failed to get item - Redirect on', redirectTo);
              history.push(redirectTo);
              setIsLoading(false);
            });
        } else {
          resp.json()
            .then((data: OrganizationT) => {
              setDetails(data);
              setIsLoading(false);
            });
        }
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  if (isLoading) {
    return <Loader.Page />;
  }
  return (
    <div>
      <div className="title__header">
        <h1 className="text-first-letter-uppercase">{ `Organization · ${organizationName}` }</h1>
        <div className="title__header--actions">
          <ButtonActionActiveStatus
            apiCall={api.organizations}
            targetId={organizationName as string}
            defaultValue={details?.active as boolean}
            name="organization"
          />
          <Link to={`/organizations/${organizationName}/edit`}>
            <Button variant="contained" color="secondary">Edit</Button>
          </Link>
          <ButtonActionDeleteEnforceConfirm
            targetId={organizationName as string}
            targetName={organizationName as string}
            api={api.organizations}
            messageComplement={`this organization named '${details?.displayName}'`}
            redirectLocation="/organizations"
          />
          <a href={`http${isProductionEnv() ? 's' : ''}://${details?.name}.${window.location.host}`} target="_blank" rel="noreferrer">
            <Button variant="outlined" color="success" startIcon={<VpnKeyIcon />}>Login</Button>
          </a>
        </div>
      </div>
      <TabsOrganize tabs={tabsContent} />
    </div>
  );
}

export default OrganizationsDetails;
